import React from "react";
import ContentEditor from "@/admin/ContentEditor";
import { FieldLabel, MediaField, TextField } from "./Fields";
import { looksLikeImageKey, looksLikeImageUrl, looksLikeMultiline } from "./helpers";
import { Plus, Trash2 } from "lucide-react";

const ArrayEditor = ({ label, value, onChange, keyName, path }) => {
  const arr = Array.isArray(value) ? value : [];
  const itemsAreObjects = arr.length > 0 && typeof arr[0] === "object" && !Array.isArray(arr[0]) && arr[0] !== null;

  const set = (next) => onChange(next);
  const add = () => {
    if (itemsAreObjects) {
      const template = arr[0]
        ? Object.fromEntries(Object.keys(arr[0]).map((k) => {
            const v = arr[0][k];
            if (typeof v === "boolean") return [k, false];
            if (typeof v === "number") return [k, 0];
            if (Array.isArray(v)) return [k, []];
            if (v && typeof v === "object") return [k, {}];
            return [k, ""];
          }))
        : {};
      set([...arr, template]);
    } else {
      set([...arr, ""]);
    }
  };
  const remove = (i) => set(arr.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    const next = [...arr];
    [next[i], next[j]] = [next[j], next[i]];
    set(next);
  };
  const update = (i, v) => set(arr.map((x, idx) => (idx === i ? v : x)));

  return (
    <div data-testid={`array-${path}`}>
      {label && (
        <FieldLabel>
          {label} <span className="text-zinc-400 normal-case">· {arr.length} item{arr.length === 1 ? "" : "s"}</span>
        </FieldLabel>
      )}
      <div className="space-y-2">
        {arr.map((item, i) => (
          <div key={i} className="flex items-start gap-2 group">
            <div className="flex-1 border border-zinc-200 rounded bg-white px-3 py-3">
              {itemsAreObjects ? (
                <ContentEditor value={item} onChange={(v) => update(i, v)} path={`${path}.${i}`} compact />
              ) : looksLikeImageKey(keyName) || (typeof item === "string" && looksLikeImageUrl(item)) ? (
                <MediaField value={item} onChange={(v) => update(i, v)} kind="image" testid={`field-${path}-${i}`} />
              ) : (
                <TextField value={item} onChange={(v) => update(i, v)} multiline={looksLikeMultiline(item)} testid={`field-${path}-${i}`} />
              )}
            </div>
            <div className="flex flex-col gap-1 pt-2">
              <button type="button" onClick={() => move(i, -1)} className="text-zinc-400 hover:text-zinc-700 text-xs px-1" title="Move up">↑</button>
              <button type="button" onClick={() => move(i, 1)} className="text-zinc-400 hover:text-zinc-700 text-xs px-1" title="Move down">↓</button>
              <button type="button" onClick={() => remove(i)} className="text-red-500 hover:text-red-700" data-testid={`array-remove-${path}-${i}`} title="Remove">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={add}
          className="flex items-center gap-1.5 text-xs text-zinc-700 hover:text-zinc-900 border border-dashed border-zinc-300 hover:border-zinc-500 rounded px-3 py-2 transition"
          data-testid={`array-add-${path}`}
        >
          <Plus size={14} /> Add item
        </button>
      </div>
    </div>
  );
};

export default ArrayEditor;
